/**
 * Mock Mode Banner Component
 *
 * Displays a clear visual indicator when mock mode is enabled.
 * Shows in top-right corner with ability to disable mock mode.
 *
 * DESIGNED TO BE REMOVABLE: Delete this file when mock mode is removed.
 */
/* eslint-disable react/jsx-no-bind, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */

import {
  MOCK_MODE_ENABLED,
  disableMockMode,
  getMockModeStatus,
  clearMockData,
} from '@ox/visits-ui/config/mockMode';
import { useState } from 'preact/hooks';
import { showAlert, showConfirm } from '@ox/visits-ui/utils/dialog';

export function MockModeBanner() {
  const [showMenu, setShowMenu] = useState(false);
  const status = getMockModeStatus();

  // Don't render if mock mode is disabled
  if (!MOCK_MODE_ENABLED) {
    return null;
  }

  const handleDisable = async () => {
    const confirmed = await showConfirm(
      'Are you sure you want to disable mock mode? This will reload the page.',
    );
    if (confirmed) {
      disableMockMode();
    }
  };

  const handleClearData = async () => {
    const confirmed = await showConfirm(
      'Are you sure you want to clear all mock data? This will delete all mock drafts and submissions.',
    );
    if (confirmed) {
      clearMockData();
      await showAlert('Mock data cleared successfully!');
      window.location.reload();
    }
  };

  const handleToggleMenu = () => {
    setShowMenu(!showMenu);
  };

  return (
    <>
      {/* Banner - Bottom Right */}
      <div className="mock-mode-banner" onClick={handleToggleMenu} title="Click to open mock mode menu">
        <span className="mock-mode-emoji">🧪</span>
        MOCK MODE
        <span className="mock-mode-source">
          ({status.source === 'environment' ? 'env' : 'localStorage'})
        </span>
        <span className="mock-mode-arrow">{showMenu ? '▼' : '▲'}</span>
      </div>

      {/* Dropdown Menu - Opens Upward */}
      {showMenu && (
        <>
          {/* Backdrop to close menu */}
          <div className="mock-mode-backdrop" onClick={() => setShowMenu(false)} />

          {/* Menu */}
          <div className="mock-mode-menu">
            {/* Info */}
            <div className="mock-mode-menu-info">
              <div className="mock-mode-menu-title">
                <strong>Mock Mode Active</strong>
              </div>
              <div className="mock-mode-menu-detail">
                Source: {status.source === 'environment' ? 'Environment variable' : 'localStorage'}
              </div>
              <div className="mock-mode-menu-detail">All API calls and auth are mocked</div>
            </div>

            {/* Navigation Buttons */}
            <div className="mock-mode-menu-navigation">
              <div className="mock-mode-nav-label">Quick Navigation:</div>
              <a href="/" className="mock-mode-nav-button">
                <span className="button-emoji">🏠</span> Landing Page
              </a>
              <a href="/form" className="mock-mode-nav-button">
                <span className="button-emoji">📝</span> Form (with auth)
              </a>
              <a href="/expired" className="mock-mode-nav-button">
                <span className="button-emoji">⏰</span> Expired Token Page
              </a>
              <a href="/success" className="mock-mode-nav-button">
                <span className="button-emoji">✅</span> Success Page
              </a>
            </div>

            {/* Actions */}
            <div className="mock-mode-menu-actions">
              <button type="button" onClick={handleClearData} className="mock-mode-clear">
                <span className="button-emoji">🗑️</span> Clear Mock Data
              </button>

              <button type="button" onClick={handleDisable} className="mock-mode-disable">
                <span className="button-emoji">❌</span> Disable Mock Mode
              </button>
            </div>

            {/* Help text */}
            <div className="mock-mode-menu-help">
              <strong>Mock Mode Features:</strong>
              <ul>
                <li>No backend API required</li>
                <li>JWT token-based authentication</li>
                <li>Client-side file uploads</li>
                <li>localStorage persistence</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </>
  );
}
